import { useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import { DEFAULT_PERSONA, type PersonaOverrides } from '@/features/decisions/config/personaContext';
import { useDecisionsContext } from '@/features/decisions/providers/DecisionsProvider';
import { parseExperimentIds } from '@/features/decisions/utils/fcfFormat';

const SHARED_KEYS = new Set(['period_from', 'period_to', 'scenario_run_id', 'run_id', 'ref_experiment_id']);

// Resolves the ActivePersonaContext from URL params over DEFAULT_PERSONA so
// every Replenishment page reads the same source of truth. Returns the parsed
// comparison ids plus a setter that writes a single param back to the URL.
export function useActivePersonaContext(overrides: PersonaOverrides = {}) {
  const { get: ctxGet, set: ctxSet } = useDecisionsContext();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL first, so a page is addressable: paste or refresh a link and it shows what the link
  // says. Shared keys also live in React context, which only supplies them when the URL does
  // not — that happens on cross-section navigation, where LinkPreserveSearch deliberately drops
  // the search string (Decisions → Forecasts and back).
  //
  // Safe as first choice because `setUrlParam` writes the URL for shared keys too, so an in-app
  // change is reflected in the address rather than being masked by a stale one.
  const get = (key: string, fallback: string) =>
    SHARED_KEYS.has(key)
      ? (searchParams.get(key) ?? ctxGet(key as Parameters<typeof ctxGet>[0]) ?? fallback)
      : (searchParams.get(key) ?? fallback);

  const scenarioRunId = get('scenario_run_id', DEFAULT_PERSONA.scenarioRunId);
  const runId = get('run_id', DEFAULT_PERSONA.runId);
  const refExperimentId = get('ref_experiment_id', DEFAULT_PERSONA.refExperimentId);

  const defaultExperimentRaw = (overrides.experimentIds ?? DEFAULT_PERSONA.experimentIds).join(', ');
  const experimentIdRaw = get('experiment_id', defaultExperimentRaw);
  const experimentIds = useMemo<string[]>(() => parseExperimentIds(experimentIdRaw), [experimentIdRaw]);

  const periodFrom = get('period_from', DEFAULT_PERSONA.periodFrom);
  const periodTo = get('period_to', DEFAULT_PERSONA.periodTo);

  const sku = overrides.sku !== undefined ? get('sku', overrides.sku) : undefined;
  const site = overrides.site !== undefined ? get('site', overrides.site) : undefined;

  const keystoneOnlyOverride = get('keystone_only_override', 'false') === 'true';
  const overrideValue =
    Number(get('keystone_only_override_value', String(DEFAULT_PERSONA.overrideValue))) || DEFAULT_PERSONA.overrideValue;

  const chartMultiplier =
    Number(get('chart_multiplier', String(DEFAULT_PERSONA.chartMultiplier))) || DEFAULT_PERSONA.chartMultiplier;

  // Always writes the URL, so the address stays a faithful description of what is on screen and
  // can be copied at any point. Shared keys are mirrored into context as well, to survive the
  // cross-section navigation where the search string is dropped.
  //
  // Takes several keys at once because each call is one navigation: two calls in the same render
  // both compute from the same starting params, and the second drops the first. Observed, and a
  // functional updater does not fix it.
  //
  // Shared keys replace the history entry, non-shared keys push. That keeps both behaviours as
  // they were: shared keys used to write context only and so added no history, and writing them
  // to the URL would otherwise stack one entry per field edited in the settings panel. Non-shared
  // keys (tab, policy) always pushed, and Back should still undo them.
  const setUrlParams = (entries: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(entries)) {
      if (SHARED_KEYS.has(key)) {
        ctxSet(key as Parameters<typeof ctxSet>[0], value || undefined);
      }
      if (value) next.set(key, value);
      else next.delete(key);
    }
    const replace = Object.keys(entries).every((key) => SHARED_KEYS.has(key));
    setSearchParams(next, { replace });
  };

  const setUrlParam = (key: string, value: string) => setUrlParams({ [key]: value });

  return {
    scenarioRunId,
    runId,
    refExperimentId,
    experimentIdRaw,
    experimentIds,
    periodFrom,
    periodTo,
    sku,
    site,
    keystoneOnlyOverride,
    overrideValue,
    chartMultiplier,
    setUrlParam,
    setUrlParams,
    searchParams,
  };
}
